import {
    ContractContextProvider,
    useContract,
    useContractContext,
} from 'components/contract/contractContext';
import {
    CollectionProvider,
    CollectionState,
    useCollectionContext,
} from 'components/settings/collectionContext';
import { DocumentProvider } from 'components/settings/documentContext';
import { Completion } from 'components/settings/languageCompletion';
import RestoreIcon from '@mui/icons-material/Restore';
import {
    Account,
    accountConverter,
    ACCOUNT_COLLECTION_NAME,
    Commit,
    commitCoverter,
    COMMIT_COLLECTION_NAME,
    Contract,
    contractConverter,
    CONTRACT_COLLECTION_NAME,
    Country,
    countryConverter,
    COUNTRY_COLLECTION_NAME,
    Language,
    languageConverter,
    LANGUAGE_COLLECTION_NAME,
    Market,
    translationCoverter,
    TRANSLATIONS_COLLECTION_NAME,
    userProfileCoverter,
    USER_PROFILE_COLLECTION_NAME,
} from 'models';
import { NavLink, useParams } from 'react-router-dom';
import { doc, orderBy, Timestamp, updateDoc, where } from 'firebase/firestore';
import {
    ContractCommitsCollector,
    ContractCommitsProvider,
} from 'components/settings/contractCommitsContext';
import { Button } from 'components/button';
import Modal from 'react-modal';

import './contractLanguagesPage.css';
import { useState } from 'react';
import { DateGroup, makeDateGroups } from 'components/commitsPage/commitsPage';
import { CommitsPageContextProvider } from 'components/commitsPage/commitsPageContext';
import { useCommitChanges } from 'commits';
import { useAccount } from 'components/settings/userProfileContext';
import { CountryAndLanguage } from 'components/countryAndLanguage';
import { db } from 'firebaseStore';

Modal.setAppElement('#modal');

export const useLanguage = (indexId: string) => {
    const [, , , collectionMap] = useCollectionContext(LANGUAGE_COLLECTION_NAME);
    return collectionMap.get(indexId) as Language | undefined;
};

export const useCountry = (indexId: string) => {
    const [, , , collectionMap] = useCollectionContext(COUNTRY_COLLECTION_NAME);
    return collectionMap.get(indexId) as Country | undefined;
};

export const ContractLanguagesPage = () => {
    const { contractId } = useParams();
    if (!contractId) {
        return <div>`Cannot load contract with id '${contractId}'`</div>;
    }
    return (
        <CollectionProvider collectionName={ACCOUNT_COLLECTION_NAME} converter={accountConverter}>
            <DocumentProvider
                collectionName={CONTRACT_COLLECTION_NAME}
                converter={contractConverter}
                id={contractId}
            >
                <ContractLanguagesPageWrapper />
            </DocumentProvider>
        </CollectionProvider>
    );
};

const ContractLanguagesPageWrapper = () => {
    const [contract, pending] = useContract();
    if (pending) {
        return <div>Loading contract</div>;
    }
    if (!contract) {
        return <div>Could not load contract</div>;
    }
    return (
        <ContractCommitsProvider indexIds={contract.getIndexIds()}>
            <CollectionProvider
                collectionName={COUNTRY_COLLECTION_NAME}
                converter={countryConverter}
            >
                <CollectionProvider
                    collectionName={COMMIT_COLLECTION_NAME}
                    converter={commitCoverter}
                    constraints={[
                        where('targetIndexId', '==', contract.indexId()),
                        orderBy('createdAt', 'asc'),
                    ]}
                >
                    <CollectionProvider
                        collectionName={TRANSLATIONS_COLLECTION_NAME}
                        converter={translationCoverter}
                        keyFunction={(translation) =>
                            `${translation.languageIndexId}-${translation.targetId}`
                        }
                    >
                        <CollectionProvider
                            collectionName={LANGUAGE_COLLECTION_NAME}
                            converter={languageConverter}
                        >
                            <ContractContextProvider>
                                <ContractCommitsCollector
                                    indexIds={contract.getIndexIds()}
                                    contents={[]}
                                >
                                    {(contents: CollectionState<Commit>[]) => (
                                        <ContractLanguagesPageContent
                                            commits={contents
                                                .map(([commits]) => commits)
                                                .flat()
                                                .filter((commit) => commit.show())}
                                        />
                                    )}
                                </ContractCommitsCollector>
                            </ContractContextProvider>
                        </CollectionProvider>
                    </CollectionProvider>
                </CollectionProvider>
            </CollectionProvider>
        </ContractCommitsProvider>
    );
};

const getLastReview = (commits: Commit[]) => {
    return new Map(
        commits
            .filter((c) => c.action === 'review')
            .map((commit) => [commit.languageIndexId, commit])
    );
};

const getCommitsToReview = (reviews: Map<string | null, Commit>, commits: Commit[]) => {
    const commitsToReview = new Map<string | null, Commit[]>();
    commits
        .filter((commit) => {
            const lastReview = reviews.get(commit.languageIndexId);
            return (
                !lastReview || (commit.createdAt as Timestamp) > (lastReview.createdAt as Timestamp)
            );
        })
        .map((commit) => {
            const commits = commitsToReview.get(commit.languageIndexId) || [];
            return commitsToReview.set(commit.languageIndexId, commits.concat([commit]));
        });
    return commitsToReview;
};

const ContractLanguagesPageContent = ({ commits }: { commits: Commit[] }) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [marketModalIsOpen, setMarketModalIsOpen] = useState(false);
    const [countryIndexId, setCountryIndexId] = useState<string>('');
    const [languageIndexId, setLanguageIndexId] = useState<string>('');
    const account = useAccount() as Account;
    const { contract, setContract } = useContractContext();
    const [, , , translationsMap] = useCollectionContext(TRANSLATIONS_COLLECTION_NAME);
    const markets = (account?.getMarkets() || []).concat(contract.markets);
    const targetIds = contract.getIndexIds().map((indexId) => indexId.split('_').splice(-1)[0]);
    const languageIndexIds = markets.map((market) => market.languageIndexId);
    const [reviewCommits] = useCollectionContext(COMMIT_COLLECTION_NAME, [
        where('targetIndexId', '==', contract.indexId()),
        orderBy('createdAt', 'asc'),
    ]) as CollectionState<Commit>;
    const reviews = getLastReview(reviewCommits);
    const toReview = getCommitsToReview(reviews, commits);
    const complete = languageIndexIds
        .map((languageIndexId) =>
            targetIds
                .map((targetId) => !!translationsMap.get(`${languageIndexId}-${targetId}`)?.content)
                .filter((i) => i)
        )
        .flat().length;
    const onSave = (newerContract: Contract) => {
        const docRef = doc(db, CONTRACT_COLLECTION_NAME, contract.id);
        return updateDoc(docRef, newerContract.toFirestore()).then(
            (x) => {
                setContract(newerContract);
                Promise.resolve();
            },
            (error) => {
                console.error('ContractLanguagesPageContent updateDoc error', error);
            }
        );
    };
    return (
        <div
            style={{
                margin: '0px 70px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <Review
                modalIsOpen={modalIsOpen}
                setModalIsOpen={setModalIsOpen}
                toReview={toReview.get(null) || []}
                languageIndexId={null}
            />
            <Modal
                isOpen={marketModalIsOpen}
                style={{
                    content: {
                        width: 'calc(100vw - 300px)',
                        maxHeight: 'calc(100vh - 200px)',
                        height: '',
                        top: '50%',
                        left: '50%',
                        right: 'auto',
                        bottom: 'auto',
                        padding: '40px',
                        marginRight: '-50%',
                        transform: 'translate(-50%, -50%)',
                        fontFamily: 'Abel',
                        backgroundColor: '#fbfbfb',
                    },
                }}
                onRequestClose={() => setMarketModalIsOpen(false)}
            >
                <CountryAndLanguage
                    countryIndexId={countryIndexId}
                    setCountryIndexId={setCountryIndexId}
                    languageIndexId={languageIndexId}
                    setLanguageIndexId={setLanguageIndexId}
                    onSave={async () => {
                        if (!countryIndexId || !languageIndexId) {
                            return;
                        }
                        await onSave(
                            new Contract({
                                ...contract,
                                markets: [...contract.markets, { countryIndexId, languageIndexId }],
                            })
                        );
                        setMarketModalIsOpen(false);
                        setCountryIndexId('');
                        setLanguageIndexId('');
                    }}
                    onCancel={() => setMarketModalIsOpen(false)}
                />
            </Modal>
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ fontSize: '24px' }}>{contract.name}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <NavLink to={`/contract/${contract.id}/commits`} className="plainNavLink">
                        <div
                            className="historyButton"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                borderRadius: '8px',
                                padding: '1px 5px',
                            }}
                        >
                            <RestoreIcon />
                            <div style={{ marginLeft: '5px' }}>Change History</div>
                        </div>
                    </NavLink>

                    <Button
                        style={{ marginLeft: '16px', width: '97px' }}
                        onClick={() => setMarketModalIsOpen(true)}
                    >
                        Add
                    </Button>
                </div>
            </div>
            <div style={{ width: '100%', marginBottom: '20px' }}>
                <Completion
                    total={
                        targetIds.length *
                        contract.markets.map((market) => market.languageIndexId).length
                    }
                    complete={complete}
                    barWidth="100%"
                />
            </div>
            <div style={{ width: '100%', backgroundColor: '#fbfbfb' }}>
                {markets.map((market) => (
                    <MarketItem
                        key={`${market.languageIndexId}${market.countryIndexId}`}
                        market={market}
                        targetIds={targetIds}
                        toReview={toReview.get(market.main ? null : market.languageIndexId) || []}
                    />
                ))}
            </div>
        </div>
    );
};

const Review = ({
    modalIsOpen,
    setModalIsOpen,
    toReview,
    languageIndexId,
}: {
    languageIndexId: string | null;
    modalIsOpen: boolean;
    setModalIsOpen: (_: boolean) => void;
    toReview: Commit[];
}) => {
    const { contract } = useContractContext();
    const commitChanges = useCommitChanges();
    return (
        <Modal
            isOpen={modalIsOpen}
            style={{
                content: {
                    width: 'calc(100vw - 300px)',
                    maxHeight: 'calc(100vh - 200px)',
                    height: '',
                    top: '50%',
                    left: '50%',
                    right: 'auto',
                    bottom: 'auto',
                    padding: '40px',
                    marginRight: '-50%',
                    transform: 'translate(-50%, -50%)',
                    fontFamily: 'Abel',
                    backgroundColor: '#fbfbfb',
                },
            }}
            onRequestClose={() => setModalIsOpen(false)}
        >
            <CollectionProvider
                collectionName={USER_PROFILE_COLLECTION_NAME}
                converter={userProfileCoverter}
                keyFunction={(r) => r.email}
            >
                <CommitsPageContextProvider>
                    {makeDateGroups(toReview).map((dateGroup) => (
                        <DateGroup {...dateGroup} key={dateGroup.daysAgo} />
                    ))}
                </CommitsPageContextProvider>
            </CollectionProvider>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    style={{ width: '175px', marginTop: '20px' }}
                    onClick={async () => {
                        await commitChanges({
                            target: contract,
                            action: 'review',
                            languageIndexId,
                            message: '',
                            changes: [
                                {
                                    field: 'review',
                                    before: null,
                                    after: null,
                                    beforeValues: null,
                                    afterValues: null,
                                },
                            ],
                        });
                        setModalIsOpen(false);
                    }}
                >
                    Confirm {toReview.length} Change{toReview.length === 1 ? '' : 's'}
                </Button>
            </div>
        </Modal>
    );
};

const MarketItem = ({
    market,
    targetIds,
    toReview,
}: {
    market: Market;
    targetIds: string[];
    toReview: Commit[];
}) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const { contract } = useContractContext();
    const language = useLanguage(market.languageIndexId);
    const country = useCountry(market.countryIndexId);
    const [, , , translationsMap] = useCollectionContext(TRANSLATIONS_COLLECTION_NAME);
    const total = targetIds.length;
    const complete = targetIds
        .map((targetId) => !!translationsMap.get(`${market.languageIndexId}-${targetId}`)?.content)
        .filter((i) => i).length;
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
            <Review
                modalIsOpen={modalIsOpen}
                setModalIsOpen={setModalIsOpen}
                languageIndexId={market.main ? null : market.languageIndexId}
                toReview={toReview}
            />
            <NavLink
                className="itemRecord"
                to={
                    market.main
                        ? `/contract-clauses/${contract.id}`
                        : `/contract-languages/${contract.id}/language/${language?.id}`
                }
                style={{
                    textDecoration: 'unset',
                    color: 'unset',
                    justifyContent: 'space-between',
                    paddingLeft: '40px',
                    flexGrow: 1,
                }}
            >
                <div style={{ display: 'flex' }}>
                    <div style={{ width: '120px' }}>{country?.name}</div>
                    <div style={{ width: '120px' }}>{language?.name}</div>
                    <div>{market.main && 'Main account version'}</div>
                </div>
            </NavLink>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                {!!toReview.length && (
                    <Button
                        style={{ marginRight: '16px', width: '175px', height: '24px', flexGrow: 1 }}
                        theme={toReview.length ? 'orange' : 'disabled'}
                        onClick={() => setModalIsOpen(true)}
                    >
                        Review {toReview.length} Change{toReview.length === 1 ? '' : 's'}
                    </Button>
                )}
                <div>
                    {!market.main && (
                        <Completion total={total} complete={complete} barWidth="80px" />
                    )}
                </div>
            </div>
        </div>
    );
};
